
<script>
import { defineComponent, computed, ref, onMounted, inject, onBeforeUnmount, watch } from 'vue'
import { actionSetOA2T, actionOA2T } from '@kit/utils/Animate'
import { mergeClassesTheme, mergeStylesTheme } from '@kit/utils/Formats'
import MouseOut from '@kit/utils/MouseOut'
import anime from 'animejs'
import Img from '@kit/components/Img.vue'
import Config from '@kit/utils/Config'

/* 

A neat rollover information area with a background image. 
This takes a lot of setup, admittedly, but here's an example where we're wiring a bunch of properties to CMS-fed data.

  <RolloverInfoArea aria-role="list-item" 
    tag="router-link"
    :to="`/family/${family.url_slug}`"
    outerClasses="sb-v rolloverinfoarea-outer sb-uniform sb-gr sb-g10"
    :theme="rolloverInfoTheme_" 
    :imgAriaLabel="family.primary_flower_image[0].aria_label"
    :imgAriaDescribedBy="`home-family-area-${i}-${j}-on home-family-area-${i}-${j}-off`"
    :src="cmsasset(family.primary_flower_image[0].image.guid)"
    :coverCSS="`background-color:${family.primary_flower_image[0].rollover_rgba}`"
    >
      <template v-slot:onContent>
        <p :id="`home-family-area-${i}-${j}-on`" class="sb sb-text flower-tile-main-text" v-html="family.short_text"></p>
      </template>
      <template v-slot:offContent>
        <div :id="`home-family-area-${i}-${j}-off`" class="sb sb-v" :style="`background-color:${family.primary_flower_image[0].rollover_title_rgba}; color:${family.primary_flower_image[0].rollover_title_color || '#FFFFFF' }; padding:20px; margin:0px;`">
          <h3 class="sb sb-text"><b>{{ family.display_title }}</b></h3>
          <h3 class="sb sb-text" style="margin-top:0px; font-size:1.1rem;">{{ family.primary_flower_image[0].display_title }}</h3>
        </div>
      </template>
  </RolloverInfoArea> 

And here's an example theme:

const rolloverInfoTheme = {
  outerClasses:"sb-v aboutus-rollover-area sb-explicit sb-gr sb-g10",
  outerCSS:"padding:0px;",
  coverType:"funky",
  coverCSS: `background-color:rgba(0,0,0,0.4)`, 
  coverRows:5,
  coverCols:5,
  onContentClasses: "sb-v sb-alt2 sb-align-c sb-g20",
  offContentClasses: "sb-v sb-align-2bottom sb-gr sb-g20 cool-rolloverinfoarea-offContent",
  imgAriaLabel:"flower picture",
  imgTheme: sunriseProteaRolloverImg
}


properties: 

src: required. the image source.

theme: {

  outerClasses. optional. The mergeable outer classes for the object
  
  outerCSS. optional. The outer css style for the object
  
  duration. optional. The duration of animation. Default is 500 
  
  coverStyle. optinal. funky or normal. Default is funky
  
  coverDirection. optional. Which direction. Up, down, right, left. For normal coverStyle
  
  coverCSS, optional. The css to apply to the rollover cover, and it also applies to the "funky" elements. default is rgba(0,0,0,0.5)
  
  coverRows. optional. default is 5. For funky coverstyle
  
  coverCols. optional. defaul is 5. For funky coverstyle
  
  onContentClasses. optional. The mergeable classes for the onContent wrapper
  
  offContentClasses. optional. The mergeable classes for the onContent wrapper
    
  imgStyle. optional. the style for the image

  imgClass. optional. the class for the image

  imgAriaLabel. optional. The aria label.

  imgAriaRole. optiona. The aria-role for the image. Default to img

  imgAriaDescribedBy. optional. the aria-describedby attribute. 

  imgCoverClasses. optional. The mergeable classes for the image cover. 
  
  imgCoverCSS. optional. The styles for the image cover.
   
  imgTheme: optional. default is null. The theme for the lazy-loading image

}


*/

const defaultConfig = new Config({
  tag:"div",

  outerClasses:"sb-v rolloverinfoarea-outer sb-explicit sb-gr sb-g10",
  outerCSS:"padding:0px;",
  coverType:"funky",
  coverCSS: `background-color:rgba(0,0,0,0.4)`, 
  coverRows:5,
  coverCols:5,
  onContentClasses: "sb-v sb-alt2 sb-align-c sb-g20",
  offContentClasses: "sb-v sb-align-2bottom sb-gr sb-g20 rolloverinfoarea-offContent",

  imgCoverClasses: null,
  imgCoverCSS: null,
  imgStyle:"margin-top:0px;",
  imgClass: null,
  imgAriaLabel: null,
  imgAriaRole:"img",
  imgAriaDescribedBy:null,

  imgTheme: {
    display: 'fit',
    fit:'cover',
    position:'center',
    width:'100%',
    aspect:1,
  }
})

export default defineComponent({
  name: "RolloverInfoImg",
  props:[
    "theme", 

    "tag",
    "outerClasses",
    "outerCSS",
    "coverType",
    "coverCSS",
    "coverRows",
    "coverCols",
    "onContentClasses",
    "offContentClasses",

    "src", 
    "imgCoverClasses",
    "imgCoverCSS",
    "imgStyle",
    "imgClass",
    "imgAriaLabel",
    "imgAriaRole",
    "imgAriaDescribedBy",
    "imgTheme"
  ],

  setup(props, context) {

    let mouseOut = null
    const funky = ref(null)
    const funky2 = ref(null)
    const root = ref(null)
    const offContent = ref(null)
    const onContent = ref(null)

    const themer = inject("themer")
    const { getProp, setProp } = themer({ props, defaultConfig })

    //Animation for the nav-bar to go to scrolling mode.
    const funkyActions = actionSetOA2T([
      {
        name:"mouseOver",
        element(container) {
          return container
        },
        async action(container) {
          const childNodes = container.childNodes 
          const allNodes = []
          let numComplete = 0
          const numAnimations = getProp('coverCols')*getProp('coverRows')+2
    
          await new Promise((resolve) => {

            if(!funky2.value || !onContent.value || !offContent.value) {
              return
            }

            const onContentEl = onContent.value
            const offContentEl = offContent.value

            onContentEl.style.display = "flex"
            onContentEl.style.opacity = 0;
            onContentEl.style.transform = "translateY(50px)"

            anime({
              targets:onContentEl,
              translateY:"0px",
              opacity:1,
              duration:500,
              easing:"easeOutQuad",
              complete() {
                numComplete++
                if(numComplete == numAnimations) {
                  resolve()
                }
              }
            })
            anime({
              targets:offContentEl,
              translateY:"-50px",
              opacity:0,
              duration:500,
              easing:"easeOutQuad",
              complete() {
                numComplete++
                offContentEl.style.display = "none"
                if(numComplete == numAnimations) {
                  resolve()
                }
              }
            })

            if(!funky2.value || !onContent.value || !offContent.value) {
              return
            }
            
            funky2.value.style.display = "none"

            for(let i=0; i<childNodes.length; i++) {
              const node = childNodes[i]
              if(node.tagName == "DIV") {
                const alexTop = parseInt(node.getAttribute("data-alextop"))
                allNodes.push(node)

                const coverRows = getProp('coverRows')

                node.style.top = (alexTop-1)*(100/coverRows)+"%"
                node.style.display = "inline"
                node.style.transform = "scale(0) rotate(0)"
                node.style.opacity = 0

                anime({
                  targets:node,
                  scale:1,
                  rotate:180,
                  duration:500,
                  opacity:1,
                  easing:"easeOutQuad",
                  complete() {
                    numComplete++
                    if(numComplete == numAnimations) {
                      resolve()
                    }
                  }
                })
              }
            }
          })

          if(!funky2.value || !onContent.value || !offContent.value) {
            return
          }

          for(let i=0; i<allNodes.length; i++) {
            const node = allNodes[i]
            node.style.display = "none"
          }
          funky2.value.style.display = "inline"
        }
      },
      {
        name:"mouseOut",
        element(container) {
          return container
        },
        async action(container) {

          let numComplete = 0
          const numAnimations = getProp('coverCols')*getProp('coverRows')+2
    
          await new Promise((resolve) => {

            if(!funky2.value || !onContent.value || !offContent.value) {
              return
            }

            const childNodes = container.childNodes 
            funky2.value.style.display = "none"

            const onContentEl = onContent.value
            const offContentEl = offContent.value

            offContentEl.style.display = "flex"
            offContentEl.style.opacity = 0;
            offContentEl.style.transform = "translateY(-50px)"

              anime({
                targets:onContentEl,
                translateY:"50px",
                opacity:0,
                duration:500,
                easing:"easeOutQuad",
                complete() {
                  onContentEl.style.display = "none"
                  numComplete++
                  if(numComplete == numAnimations) {
                    resolve()
                  }
                }
              })
              anime({
                targets:offContentEl,
                translateY:"0px",
                opacity:1,
                duration:500,
                easing:"easeOutQuad",
                complete() {
                  numComplete++
                  if(numComplete == numAnimations) {
                    resolve()
                  }
                }
              })

            const coverRows = getProp('coverRows')

            for(let i=0; i<childNodes.length; i++) {
              const node = childNodes[i]
              if(node.tagName == "DIV") {
                const alexTop = parseInt(node.getAttribute("data-alextop"))
                node.style.top = (alexTop-1)*(100/coverRows)+"%"
                node.style.display = "inline"
                anime({
                  targets:node,
                  scale:0,
                  opacity:0,
                  duration:300,
                  easing:"easeOutQuad",
                  complete() {
                    numComplete++
                    if(numComplete == numAnimations) {
                      resolve()
                    }
                  }
                })
              }
            }
          })
        }
      },
    ], { dualSet:true })

    const goFunkyRollover = () => {
      actionOA2T(funkyActions, "mouseOver", [funky.value])
    }
    const goFunkyRollout = () => {
      actionOA2T(funkyActions, "mouseOut", [funky.value])
    }

    //watch for when the root appears
    watch(root, (_newVal, _oldVal) => {
      if(root.value) {
        addOrRefreshEventListeners()
      }
    })

    
    onMounted(async() => {

      if(root.value) {
        addOrRefreshEventListeners()
      }

      //ALEX-RESKIN/////
      //An experiment with retheming the lazy-images. yes this works, you can do this
      //on the fly.
      // Set the property and then tick up the reskin
      //await sleep(5000)
      //console.log("ALEX!!!!! Oh my did reactivity break?")
      //setProp("lazyImgTheme", lazyImgTheme2, true)
      //setProp("imgCoverCSS", `background-color:rgba(0,0,255,0.4)`, false, true)
    })
    onBeforeUnmount(() => {
      if(mouseOut) {
        mouseOut.destroy()
      }
    })

    //The style of the root will change depending on whether it's supposed 
    //to have a passive background image or whether or not it's supposed to 
    //have an active background image that "props up" the height of the component 
    const rootStyle = computed(() => {
      return mergeStylesTheme("overflow:hidden;", getProp('outerCSS'))
    })

    //ALEX
    // const littleSquareStyle = computed(() => {
    //   mergeStylesTheme(`transform:scale(0); transform-origin:center; position:absolute; top:${(i-1)*(100/getProp('coverRows'))}%; left:${(j-1)*(100/getProp('coverCols'))}%; width:${100/getProp('coverCols')}%; height:${100/getProp('coverRows')}%;`, getProp('coverCSS'))
    // })


    const addOrRefreshEventListeners = () => {

      if(!mouseOut) {
        mouseOut = new MouseOut()
      }

      if(!root.value) {
        throw new Error("No root ref found")
      }

      //Remember that the element might be $el, depending on
      const element = root.value.$el || root.value

      mouseOut.register(element, 
        function({ currentTarget }) {
          if(getProp('coverType') == "funky") {
            goFunkyRollover()
          }
        },
        function({ currentTarget }) {
          if(getProp('coverType') == "funky") {
            goFunkyRollout()
          }
        }
      )      
    }


    return { 
      getProp, 
      setProp,
      root, 
      funky,
      funky2,
      mergeClassesTheme,
      mergeStylesTheme,
      rootStyle,
      offContent,
      onContent
    }
  }, 
  components: {
    Img
  }
});
</script>

<style scoped>
.rollover--funkystyle {
  position:absolute;
}
.content-container {
  width:100% !important; 
  height:100%;
  position:absolute !important;
}
/* I had this as one of the classes on the root, but it broke things
occasionally and made everything really short. */
.fitContent {
  height:fit-content !important
}
</style>


<template>

  <component :is="getProp('tag')" ref="root" :class="mergeClassesTheme('sb', getProp('outerClasses'))" :style="rootStyle">      
      
    <Img
    :src="src" 
    :theme="getProp('imgTheme', 'theme')"
    :class="mergeClassesTheme('sb', getProp('imgClass'))" 
    :style="getProp('imgStyle')"
    :ariaLabel="getProp('imgAriaLabel')"
    :ariaRole="getProp('imgAriaRole')"
    :ariaDescribedBy="getProp('imgAriaDescribedBy')"
    />
  
    <div aria-role="none" :class="mergeClassesTheme('sb-spread', getProp('imgCoverClasses'))" :style="getProp('imgCoverCSS')"></div>
    <div aria-role="none" ref="funky" class="sb-spread" style="width:100%; height:100%; overflow:hidden;" v-if="(getProp('coverStyle') || 'funky') == 'funky'">

      <template v-for="i in (getProp('coverRows'))">
        <template v-for="j in (getProp('coverCols'))">
          <div class="rollover--funkystyle" :data-alextop="`${i}`" :style="mergeStylesTheme(`transform:scale(0); transform-origin:center; position:absolute; top:${(i-1)*(100/getProp('coverRows'))}%; left:${(j-1)*(100/getProp('coverCols'))}%; width:${100/getProp('coverCols')}%; height:${100/getProp('coverRows')}%;`, getProp('coverCSS'))"></div>
        </template>
      </template>

    </div>
    <div aria-role="none" ref="funky2" id="funky2" class="sb-spread" :style="mergeStylesTheme('display:none;', getProp('coverCSS'))" v-if="(getProp('coverStyle') || 'funky') == 'funky'"></div>
    
    <div ref="offContent" :class="mergeClassesTheme('sb sb-spread content-container', getProp('offContentClasses'))">
      <slot name="offContent"></slot>
    </div>
    <div ref="onContent" style="display:none" :class="mergeClassesTheme('sb sb-spread content-container', getProp('onContentClasses'))">
      <slot name="onContent"></slot>
    </div>

  </component>

</template>